import {
  AuditStatus,
  ProductSource,
  ProductStatus,
  SupplyMethod,
  UnitConversionOperator,
  UomUnitType,
} from '../lib/enums';
import { BaseType, ModuleAccess } from './auth.model';
import React from 'react';
import { Movement } from './MaterialRequest';

export interface FormField {
  name: string;
  label: string;
  rules?: any[];
  disabled?: boolean;
  values?: BaseModel[];
  isMultiple?: boolean;
  allowClear?: boolean;
  defaultValue?: any;
  type?: string;
  isLoading?: boolean;
  options?: { label: string; value: string }[];
}

export interface CustomFormData {
  [key: string]: any;
}

export interface PageableResponse<T> {
  start: number;
  length: number;
  recordsTotal: number;
  recordsFiltered: number;
  data: T[];
}

export interface BaseModel {
  id: string;
  createdBy: string | undefined;
  updatedBy: string | undefined;
  timeCreated: string | undefined;
  timeUpdated: string | undefined;
  code: string;
  description: string;
  active: boolean | undefined;
}

export interface WarehouseModel {
  id: string;
  createdBy: string;
  updatedBy: string;
  timeCreated: string;
  timeUpdated: string;
  active: boolean;
  code: string;
  description: string;
  address: string;
  city: BaseModel;
  country: BaseModel;
}

export interface LocationModel {
  id: string;
  createdBy: string;
  updatedBy: string;
  timeCreated: string;
  timeUpdated: string;
  active: boolean;
  code: string;
  description: string;
  address: string;
  warehouse: BaseModel;
}

export interface PartnerModel {
  id: string;
  createdBy: string;
  updatedBy: string;
  timeCreated: string;
  timeUpdated: string;
  active: boolean;
  code: string;
  description: string;
  address: string;
  city: BaseModel;
  country: BaseModel;
  lineOfBusiness: BaseModel;
  postalCode: string;
  email: string;
  phoneNo: string;
  faxNo: string;
  website: string;
  agentName: string;
  agentContact: string;
  contactFinance: string;
  contactPerson: string;
  contactShipping: string;
  // Supplier accreditation -- suppliers only; buyers share this model but
  // never carry these.
  supplierType?: string;
  materialSupplied?: string;
  yearEstablished?: number;
  ownershipType?: string;
  paymentTerms?: string;
  accredited?: boolean;
  dateAccredited?: string;
  notes: string;
}

export interface DepartmentModel {
  id: string;
  createdBy: string;
  updatedBy: string;
  timeCreated: string;
  timeUpdated: string;
  active: boolean;
  code: string;
  description: string;
  defaultLocation: BaseModel;
  categories: BaseModel[];
}

export interface LineModel {
  id: string;
  createdBy: string;
  updatedBy: string;
  timeCreated: string;
  timeUpdated: string;
  active: boolean;
  code: string;
  description: string;
  manPower: number;
  section: BaseModel;
}

export interface AppErrorResponse {
  timestamp: string;
  status: number;
  error: string;
  message: string;
  path: string;
  appErrorCode: string | null;
  errors: any[];
}

export interface UnitOfMeasureModel {
  id: string;
  createdBy: string;
  updatedBy: string;
  timeCreated: string;
  timeUpdated: string;
  active: boolean;
  code: string;
  description: string;
  unitType: UomUnitType;
  conversionType: UnitConversionOperator;
  count: number;
  factor: number;
  rate: number;
  baseUom: UnitOfMeasureModel;
}

export interface ProductModel {
  id: string;
  createdBy: string;
  updatedBy: string;
  timeCreated: string;
  timeUpdated: string;
  active: boolean;
  code: string;
  description: string;
  defaultUom?: BaseModel;
  purchaseUom?: BaseModel;
  productCategory?: BaseModel;
  supplyMethod?: SupplyMethod;
  productStatus?: ProductStatus;
  productSource?: ProductSource;
  auditStatus?: AuditStatus;
  canPurchase?: boolean;
  canSold?: boolean;
  width?: string;
  length?: string;
  height?: string;
  thickness?: string;
  weight?: string;
  purchaseCost?: number;
  salePrice?: number;
}

export const toProductMode = (data: BaseModel): ProductModel => {
  return {
    id: data.id,
    createdBy: '',
    updatedBy: '',
    timeCreated: '',
    timeUpdated: '',
    active: true,
    code: data.code,
    description: data.description,
  };
};

export interface UserFormModel {
  id: string | undefined;
  createdBy: string | undefined;
  updatedBy: string | undefined;
  timeCreated: string | undefined;
  timeUpdated: string | undefined;
  username: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  email: string | undefined;
  role: string | undefined;
  status: string | undefined;
  department: BaseType | undefined;
  moduleAccess: ModuleAccess[];
}

export interface StockLevelReport {
  id: string;
  locationCode: string;
  locationDescription: string;
  productCode: string;
  productDescription: string;
  categoryCode: string;
  categoryDescription: string;
  quantity: number;
  uomCode: string;
  uomDescription: string;
  unitPrice: number;
  totalPrice: number;
}

export interface MaterialIssuanceFilter {
  localtion_id: string | undefined | null;
  startDate: string | null;
  endDate: string | null;
  type: string | undefined | null;
  proder?: string;
  sku?: string;
  material?: string;
  datepicker?: (string | null)[];
}

export interface VendorPerformance {
  id: number;
  vendorId: number;
  vendorCode: string;
  venderDescription: string;
  pono: string;
  orderDate: string;
  requiredDate: string;
  status: string;
}

export interface MaterialStatus {
  id: number;
  pono: string;
  orderDate: string;
  requiredDate: string;
  rrDate: string;
  status: string;
}

export interface RecordFile {
  id: string;
  logRevisionId: string;
  createdBy: string;
  updatedBy: string;
  code: string;
  timeCreated: string;
  timeUpdated: string;
  active: boolean;
  description: string;
  tag: string;
  recordPath: string;
  publicURL: string;
  folder: string;
  origFilename: string;
  filename: string;
  mimeType: string;
  recordResourceId: number;
}
export interface Proder {
  id: string;
  dateEntry: string;
  requiredDate: string;
  dateshipment: string;
  proderno: string;
}

export interface StatusHistory {
  id: string;
  createdBy: string;
  timeCreated: string;
  dataId: string;
  statusDate: string;
  status: string;
  remarks: string;
}

export interface TrackingDoc {
  module: string;
  id: string | null;
  code: string;
  status: string | null;
  date: string | null;
  sourceText: string | null;
}

export interface TrackingStage {
  stage: 'BOM' | 'PR' | 'CANVASS' | 'PO' | 'RR' | 'DR';
  edgeFromPrevious: 'STRUCTURAL' | 'DERIVED' | 'INFERRED' | 'MISSING' | 'NONE';
  edgeNote: string | null;
  documents: TrackingDoc[];
  overflowCount: number;
}

export interface TrackingChain {
  anchor: { module: string; id: string; code: string };
  stages: TrackingStage[];
}

export interface TrackingRequestRow {
  id: string;
  code: string;
  status: string;
  date: string | null;
  requestedBy: string | null;
  stage: 'RAISED' | 'CANVASSED' | 'ORDERED' | 'RECEIVED' | 'DELIVERED';
  stageDate: string | null;
  daysInStage: number | null;
  counts: { canvass: number; po: number; rr: number; dr: number };
}

export type ProderProgressState =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'FINISHED'
  | 'PART_SHIPPED'
  | 'SHIPPED';

export interface ProderProcessProgress {
  process: string;
  /** Null when the proder has no active ticket for this process ("not set up"). */
  orderQty: number | null;
  qtyDone: number;
  balance: number | null;
  opsTotal: number;
  opsStarted: number;
  firstDone: string | null;
  lastDone: string | null;
  workMinutes: number | null;
  areasWithoutFactor: string[] | null;
  remainingMinutes: number | null;
  remainingEstimated: boolean | null;
  workerDays: number | null;
  areas: AreaWork[] | null;
}

export interface AreaWork {
  area: string;
  orderPcs: number;
  pieceRate: number | null;
  factor: number | null;
  factorSample: number | null;
  workMinutes: number | null;
  factorFrom: string | null;
  factorTo: string | null;
  prevFactor: number | null;
}

export interface ProderProgressRow {
  proderNo: string;
  buyerCode: string | null;
  requestedShipDate: string | null;
  firstShipDate: string | null;
  lastShipDate: string | null;
  state: ProderProgressState;
  daysLate: number | null;
  idleDays: number | null;
  hasTicket: boolean;
  /** The proder's ordered pieces, from the BOM's synced total. */
  orderQty: number | null;
  /** The ticket's ordered operation-pieces, summed over its routing. */
  ticketOrderQty: number | null;
  shippedQty: number;
  orderMismatch: boolean;
  processes: ProderProcessProgress[];
  /** Any live plan for this proder (Planning P2 slice 3). */
  planned: boolean;
  /** Latest planned end across its processes. */
  planFinish: string | null;
  /** true = re-order, false = new item, null = no SKU on its sets to check. */
  reorder: boolean | null;
  reorderEvidence: string | null;
  /** FINAL, DRAFT (final Proder not received), null = no PDMOPS record. */
  finalStatus: 'FINAL' | 'DRAFT' | null;
  finalDue: string | null;
  /** Still a draft and past its due date: reschedule. */
  finalLate: boolean;
  shipVerdict: ShipVerdict;
  confirmedShipDate: string | null;
  shipAnswerPending: boolean;
  shipDrift: boolean;
  /** Readiness gates available or not needed (of 3). */
  readinessDone: number;
  /** Readiness gates recorded at all. */
  readinessRecorded: number;
}

export type ReadinessGateKey = 'SAMPLE' | 'LAB_TEST' | 'TOOLING';
export type ReadinessStatus = 'AVAILABLE' | 'NOT_YET' | 'NOT_NEEDED';

export interface ReadinessGate {
  gate: ReadinessGateKey;
  label: string;
  defaultOwner: string;
  status: ReadinessStatus | null;
  ownerDept: string | null;
  targetDate: string | null;
  preprodRequestedOn: string | null;
  note: string | null;
  recordedBy: string | null;
  recordedAt: string | null;
  atRisk: boolean;
}

export type ShipVerdict = 'NOT_PLANNED' | 'NO_REQUEST' | 'WORKABLE' | 'NOT_WORKABLE';

export interface ShipDecisionRecord {
  id: string;
  action: 'CONFIRM_REQUESTED' | 'PROPOSE' | 'ANSWER';
  verdictAtDecision: ShipVerdict | null;
  requestedDate: string | null;
  readyDate: string | null;
  proposedDate: string | null;
  marketingAnswer: 'PENDING' | 'APPROVED' | 'DISCUSS' | null;
  answeredBy: string | null;
  answeredOn: string | null;
  confirmedDate: string | null;
  note: string | null;
  recordedBy: string | null;
  recordedAt: string | null;
}

export interface ShipDecisionRequest {
  action: 'CONFIRM_REQUESTED' | 'PROPOSE' | 'ANSWER';
  proposedDate?: string | null;
  answer?: 'APPROVED' | 'DISCUSS' | null;
  answeredBy?: string | null;
  answeredOn?: string | null;
  confirmedDate?: string | null;
  note?: string | null;
}

export interface ProderPlanSaved {
  id: string;
  workersPlanned: number;
  workMinutesUsed: number | null;
  workBasis: 'TOTAL' | 'REMAINING' | null;
  minutesDayUsed: number | null;
  daysNeeded: number | null;
  plannedStart: string;
  plannedEnd: string;
  endOverridden: boolean;
  overrideReason: string | null;
  note: string | null;
  plannedBy: string | null;
  plannedAt: string | null;
}

export interface ProderProcessPlan {
  process: string;
  workMinutesTotal: number | null;
  workMinutesRemaining: number | null;
  workBasis: 'TOTAL' | 'REMAINING' | null;
  areasWithoutFactor: string[];
  suggestion: {
    workers: number | null;
    start: string;
    startReason: string;
  };
  saved: ProderPlanSaved | null;
}

export interface ProderPlan {
  proderNo: string;
  requestedShipDate: string | null;
  planFinish: string | null;
  shipGapDays: number | null;
  daysLateVsRequested: number | null;
  minutesPerDay: number | null;
  processes: ProderProcessPlan[];
  /** No WIP routing yet (no ticket): every process is offered. */
  noRouting: boolean;
  readiness: {
    applies: boolean;
    reorder: boolean | null;
    reorderEvidence: string | null;
    done: number;
    total: number;
    gates: ReadinessGate[];
  };
  shipDecision: {
    verdict: ShipVerdict;
    requestedDate: string | null;
    readyDate: string | null;
    shipGapNotSetUp: boolean;
    daysLate: number | null;
    current: ShipDecisionRecord | null;
    drift: boolean;
  };
  finalProder: {
    status: 'FINAL' | 'DRAFT' | null;
    revisionNo: number | null;
    revisionCount: number;
    receivedOn: string | null;
    shipDateVersions: number;
    due: string | null;
    dueSetBy: string | null;
    dueSetAt: string | null;
    dueNote: string | null;
    dueSuggested: string | null;
    late: boolean;
    receivedLateDays: number | null;
  };
}

export interface ProderPlanRequest {
  workersPlanned: number;
  plannedStart: string;
  plannedEnd?: string | null;
  overrideReason?: string | null;
  note?: string | null;
}

export interface ProderProgressResponse {
  asOf: string | null;
  rows: ProderProgressRow[];
}

export interface ProderProgressDetail extends ProderProgressRow {
  tickets: {
    ticketNo: string;
    proderRev: number | null;
    process: string | null;
    area: string | null;
  }[];
  daily: {
    process: string;
    doneDate: string;
    qtyDone: number;
    workers: number;
    workMinutes: number;
  }[];
  invoices: {
    invoiceId: string;
    invoiceNo: string | null;
    shipDate: string | null;
    invoiceDate: string | null;
    shippedQty: number;
  }[];
}

export type PlanningInputSource = 'MANUAL' | 'WIP-COMPUTED';

export type PlanningInputKind =
  | 'ALLOC_WORKERS'
  | 'WORK_MINUTES_DAY'
  | 'SHIP_GAP_DAYS'
  | 'LEAD_TIME_DAYS';

export interface PlanningInputValue {
  // DELETE /planning/inputs/{id} deactivates a MANUAL row, so rows carry an id.
  id: string;
  value: number;
  source: PlanningInputSource;
  effectiveFrom: string;
  sampleSize: number | null;
  note: string | null;
  enteredBy: string | null;
  enteredAt: string | null;
}

export interface PlanningProcessInput {
  process: string;
  alloc: {
    current: PlanningInputValue | null;
    history: PlanningInputValue[];
  };
  actual: {
    workers4wk: number | null;
    avgPerDay: number | null;
    asOf: string | null;
  } | null;
  leadTime: {
    computed: PlanningInputValue | null;
    manual: PlanningInputValue | null;
    resolved: PlanningInputValue | null;
  };
}

export interface PlanningInputsResponse {
  computedAsOf: string | null;
  workMinutesDay: PlanningInputValue | null;
  shipGap: {
    computed: PlanningInputValue | null;
    manual: PlanningInputValue | null;
    resolved: PlanningInputValue | null;
  };
  processes: PlanningProcessInput[];
}

export interface SKUOrderQuantityModel {
  id: number;
  code: string;
  description: string;
  altDescription: string;
  orderQuantity: number;
}
